import { t } from '@hefe/i18n';
import { ResponseBasketballGetGameInfo } from '@hefe/models';
import { EventReportsSlice } from '@hefe/store';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import CustomForm from '../../../../core/components/form/custom-form';

export const GENERATE_EVENT_REPORT_WIZARD_SUMMARY_FORM_ID =
  'generate-event-report-wizard-summary-form';

interface GenerateEventReportWizardSummaryFormComponentProps {
  data: ResponseBasketballGetGameInfo;
}

export default function GenerateEventReportWizardSummaryFormComponent(
  props: GenerateEventReportWizardSummaryFormComponentProps
) {
  const { data } = props;

  const dispatch = useAppDispatch();
  const wizard = useSelector(EventReportsSlice.generateWizardSelector);

  const onSubmit = (values: any, resetFields?: () => void) => {
    // store values in redux and go to next step
    dispatch(
      EventReportsSlice.actions.setSummary({
        summary: values.summary,
        top_performer_home: values.top_performer_home,
        top_performer_guest: values.top_performer_guest,
      })
    );
  };

  const topPerformerHome = useMemo(() => {
    if (!data?.boxscore?.homePlayers?.length) {
      return '';
    }

    // Return name of first player
    const player = data.boxscore.homePlayers[0];

    if (player.points === 0 && player.rebounds === 0 && player.assists === 0) {
      return '';
    }

    const name = player.firstname + ' ' + player.lastname;
    const stats =
      player.points +
      'Pts ' +
      player.rebounds +
      'Rbd ' +
      player.assists +
      'Ast';

    return `${name} (${stats})`;
  }, [data]);

  const topPerformerGuest = useMemo(() => {
    if (!data?.boxscore?.guestPlayers?.length) {
      return '';
    }

    // Return name of first player
    const player = data.boxscore.guestPlayers[0];

    if (player.points === 0 && player.rebounds === 0 && player.assists === 0) {
      return '';
    }

    const name = player.firstname + ' ' + player.lastname;
    const stats =
      player.points +
      'Pts ' +
      player.rebounds +
      'Rbd ' +
      player.assists +
      'Ast';

    return `${name} (${stats})`;
  }, [data]);

  return (
    <CustomForm
      fields={[
        {
          label: t('game_reports.generate.wizard.steps.summary.game_summary'),
          name: 'summary_header',
          type: 'heading',
          rules: [],
        },
        {
          type: 'textarea',
          name: 'summary',
          label: '',
          initalValue: wizard?.data?.summary,
          additionalProps: {
            rows: 5,
          },
          rules: [],
        },
        {
          label:
            t(
              'game_reports.generate.wizard.steps.summary.top_performer.title'
            ) + data.game?.home,
          name: 'header_top_performer_home',
          type: 'heading',
          rules: [],
        },
        {
          type: 'textarea',
          name: 'top_performer_home',
          label: '',
          extraLabel: t(
            'game_reports.generate.wizard.steps.summary.top_performer.description'
          ),
          initalValue: wizard?.data?.top_performer_home ?? topPerformerHome,
          additionalProps: {
            rows: 5,
          },
          rules: [
            {
              max: 200,
              message: t('form_errors.max_length', { length: 200 }),
            },
          ],
        },
        {
          label:
            t(
              'game_reports.generate.wizard.steps.summary.top_performer.title'
            ) + data.game?.guest,
          name: 'header_top_performer_guest',
          type: 'heading',
          rules: [],
        },
        {
          type: 'textarea',
          name: 'top_performer_guest',
          label: '',
          extraLabel: t(
            'game_reports.generate.wizard.steps.summary.top_performer.description'
          ),
          initalValue: wizard?.data?.top_performer_guest ?? topPerformerGuest,
          additionalProps: {
            rows: 5,
          },
          rules: [
            {
              max: 200,
              message: t('form_errors.max_length', { length: 200 }),
            },
          ],
        },
      ]}
      onSubmit={onSubmit}
      formId={GENERATE_EVENT_REPORT_WIZARD_SUMMARY_FORM_ID}
    />
  );
}
